import React, { useEffect } from 'react';
import { FaEye, FaTrash, FaPen } from 'react-icons/fa';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Expenses/ViewExpenseDrawer.css';



const INR = (n) => `₹${(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (iso) =>
    iso ? new Date(iso).toLocaleString() : '—';

const ViewExpenseDrawer = ({ expense, onClose, onUploadBill, onEdit, onDelete, onDeleteBill }) => {


    // close on ESC
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);


    if (!expense) return null;




    const handleOverlayClick = (e) => {
        if (e.currentTarget === e.target) onClose?.();
    };

    const hasBill = !!expense?.bill?.fileUrl;

    return (
        <div className="drawer-overlay" onClick={handleOverlayClick}>
            <div className="drawer">
                <div className="drawer-header">
                    <h3>Expense: {expense.category} – {INR(expense.amount)}</h3>
                    <div className="drawer-header-actions">
                        <button
                            className="icon-btn"
                            onClick={() => onEdit?.(expense)}
                            aria-label="Edit expense"
                            title="Edit"
                            type="button"
                        >
                            <FaPen size={14} />
                        </button>

                        <button
                            className="icon-btn icon-btn--danger"
                            onClick={() => onDelete?.(expense)}
                            aria-label="Delete expense"
                            title="Delete"
                            type="button"
                        >
                            <FaTrash size={14} />
                        </button>

                        <button
                            className="drawer-close icon-btn icon-btn--ghost"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                            type="button"
                        >
                            ×
                        </button>
                    </div>
                </div>


                {/* Expense Info */}
                <div className="drawer-section">
                    <h4 className="section-title">Expense Info</h4>
                    <table className="drawer-table info-table">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Value shown</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Category</td>
                                <td><strong>{expense.category}</strong></td>
                            </tr>
                            <tr>
                                <td>Date</td>
                                <td>{fmtDate(expense.date)}</td>
                            </tr>
                            <tr>
                                <td>Amount</td>
                                <td>{INR(expense.amount)}</td>
                            </tr>
                            <tr>
                                <td>Notes</td>
                                <td>{expense.notes || '—'}</td>
                            </tr>
                            <tr>
                                <td>Paid By</td>
                                <td><strong>{expense.paidByName || expense.paidBy || 'Unknown'}</strong></td>
                            </tr>
                            <tr>
                                <td>Bill Status</td>
                                <td>
                                    {hasBill
                                        ? <span className="drawer-bill-status uploaded">✅ Uploaded</span>
                                        : <span className="drawer-bill-status pending">⚠ Pending</span>}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                
                {/* Bill Attachment */}
                <div className="drawer-section">
                    <div className="drawer-section-header">
                        <h4>Bill Attachment</h4>
                        <button
                            className="icon-text-btn"
                            onClick={() => onUploadBill(expense)}
                            type="button"
                            title={hasBill ? 'Replace bill' : 'Upload bill'}
                            aria-label={hasBill ? 'Replace bill' : 'Upload bill'}
                        >
                            {hasBill ? 'Replace' : 'Upload'}
                        </button>
                    </div>

                    {hasBill ? (
                        <div className="bill-card v2">
                            {/* Thumb / Badge */}
                            <div className="bill-thumb">
                                {String(expense.bill.mimeType || '').startsWith('image/') ? (
                                    <img src={expense.bill.fileUrl} alt={expense.bill.fileName || 'Bill image'} />
                                ) : (
                                    <div className="bill-badge">{(expense.bill.mimeType || 'file').includes('pdf') ? 'PDF' : 'DOC'}</div>
                                )}
                            </div>

                            {/* Meta */}
                            <div className="bill-meta">
                                <div className="bill-row">
                                    <span className="bill-name" title={expense.bill.fileName || 'Bill file'}>
                                        {expense.bill.fileName || 'Bill file'}
                                    </span>
                                    <span className={`bill-status ${hasBill ? 'uploaded' : 'pending'}`}>
                                        {hasBill ? 'Uploaded' : 'Pending'}
                                    </span>
                                </div>

                                <div className="bill-sub">
                                    <span>{expense.bill.mimeType || 'file'}</span>
                                    {expense.bill.uploadedAt && (
                                        <span> • Uploaded {fmtDateTime(expense.bill.uploadedAt)}</span>
                                    )}
                                    {expense.billUploadedByName && <span> • {expense.billUploadedByName}</span>}
                                    {expense.bill.billNumber && <span> • Bill # {expense.bill.billNumber}</span>}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="bill-actions">
                                <a
                                    className="icon-btn"
                                    href={expense.bill.fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="View bill"
                                    aria-label="View bill"
                                >
                                    <FaEye size={14} />
                                </a>
                                <button
                                    className="icon-btn"
                                    onClick={() => onUploadBill(expense)}
                                    title="Replace bill"
                                    aria-label="Replace bill"
                                    type="button"
                                >
                                    ⟳
                                </button>
                                <button
                                    className="icon-btn icon-btn--danger"
                                    onClick={() => onDeleteBill?.(expense)}
                                    title="Delete bill"
                                    aria-label="Delete bill"
                                    type="button"
                                >
                                    <FaTrash size={14} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bill-empty">
                            <div className="bill-empty-icon">🧾</div>
                            <div className="bill-empty-text">
                                <div className="bill-empty-title">No bill uploaded</div>
                                <div className="bill-empty-sub">Attach a PDF or image up to 10MB.</div>
                            </div>
                            <button
                                className="icon-text-btn primary"
                                onClick={() => onUploadBill(expense)}
                                type="button"
                            >
                                Upload Bill
                            </button>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ViewExpenseDrawer;
